/**
 * Tails an NDJSON file written by another process, emitting a `HarnessEvent`
 * for each newly-appended record. Built for the Claude transcript (written by
 * the `claude` CLI under the PTY) and reused for the opencode plugin-event
 * sink — the file mechanics are identical; only the record→event mapping
 * differs, so it is a constructor parameter defaulting to the Claude one.
 *
 * All reads are serialized through a promise chain so the polling interval
 * and the final `flush()` on exit can never read overlapping byte ranges.
 */

import { open } from "node:fs/promises";
import type { HarnessEvent } from "../types.js";
import { mapTranscriptRecord } from "./record-mapper.js";

const POLL_INTERVAL_MS = 25;

export class JsonlTailer {
  private offset = 0;
  private buffer = "";
  private timer: ReturnType<typeof setInterval> | null = null;
  private chain: Promise<void> = Promise.resolve();
  private _closed = false;

  constructor(
    private readonly path: string,
    private readonly onEvent: (event: HarnessEvent) => void,
    /**
     * Optional raw-record hook, invoked for EVERY parsed record — including
     * ones the mapper drops (e.g. user prompts). Used by the chat PTY proxy
     * relay (which needs between-turn records too) and by the opencode event
     * source (which owns per-turn state the pure mapper cannot).
     */
    private readonly onRawRecord?: (raw: unknown) => void,
    /** Record→event mapping; defaults to the Claude transcript mapper. */
    private readonly mapRecord: (raw: unknown) => HarnessEvent | null = mapTranscriptRecord,
  ) {}

  /**
   * Begin tailing. Pass `fromOffset` to skip bytes already present when the
   * tail starts (used on resume so the prior session's records aren't replayed).
   */
  start(fromOffset = 0): void {
    if (this.timer) return;
    this.offset = fromOffset;
    this.timer = setInterval(() => {
      if (this._closed) return;
      void this.enqueueRead();
    }, POLL_INTERVAL_MS);
  }

  close(): void {
    this._closed = true;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /** Read any remaining bytes and flush a trailing line without a newline. */
  async flush(): Promise<void> {
    await this.enqueueRead();
    if (this.buffer.length > 0) {
      this.emitLine(this.buffer);
      this.buffer = "";
    }
  }

  private enqueueRead(): Promise<void> {
    this.chain = this.chain.then(() => this.readOnce());
    return this.chain;
  }

  private async readOnce(): Promise<void> {
    let handle: Awaited<ReturnType<typeof open>> | null = null;
    try {
      handle = await open(this.path, "r");
      const stats = await handle.stat();
      if (stats.size <= this.offset) return;
      const length = stats.size - this.offset;
      const buf = Buffer.alloc(length);
      await handle.read(buf, 0, length, this.offset);
      this.offset = stats.size;
      this.consume(buf.toString("utf8"));
    } catch {
      /* transient read error (e.g. file not yet created); retry on next poll */
    } finally {
      if (handle) await handle.close();
    }
  }

  private consume(chunk: string): void {
    this.buffer += chunk;
    let index = this.buffer.indexOf("\n");
    while (index >= 0) {
      const line = this.buffer.slice(0, index);
      this.buffer = this.buffer.slice(index + 1);
      this.emitLine(line);
      index = this.buffer.indexOf("\n");
    }
  }

  private emitLine(line: string): void {
    const trimmed = line.trim();
    if (trimmed.length === 0) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return;
    }
    this.onRawRecord?.(parsed);
    const event = this.mapRecord(parsed);
    if (event) this.onEvent(event);
  }
}
